import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthForm from "@/components/AuthForm";
import LandingPage from "@/pages/LandingPage";
import PublicShop from "@/pages/PublicShop";
import BlogPage from "@/pages/BlogPage";
import FeaturesPage from "@/pages/FeaturesPage";
import LegalPage from "@/pages/LegalPage";
import OrderFromEmbed from "@/pages/OrderFromEmbed";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import Dashboard from "@/pages/Dashboard";
import ShopPage from "@/pages/ShopPage";
import PublisherDashboard from "@/pages/PublisherDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminZorki from "@/pages/AdminZorki";
import ZorkiWallet from "@/pages/ZorkiWallet";
import MailClient from "@/pages/MailClient";
import Messages from "@/pages/Messages";
import Sites from "@/pages/Sites";
import ContentGenerator from "@/pages/ContentGenerator";
import HtmlGenerator from "@/pages/HtmlGenerator";
import AiMonitor from "@/pages/AiMonitor";
import TrendingTopics from "@/pages/TrendingTopics";
import RewriteArticle from "@/pages/RewriteArticle";
import MassGenerate from "@/pages/MassGenerate";
import AffiliateProducts from "@/pages/AffiliateProducts";
import WooProducts from "@/pages/WooProducts";
import SocialMedia from "@/pages/SocialMedia";
import MobileReports from "@/pages/MobileReports";
import Comments from "@/pages/Comments";
import Forums from "@/pages/Forums";
import ApiConnectors from "@/pages/ApiConnectors";
import Extensions from "@/pages/Extensions";
import Subscription from "@/pages/Subscription";
import MediaLibrary from "@/pages/MediaLibrary";
import RssFeeds from "@/pages/RssFeeds";
import PostQueue from "@/pages/PostQueue";
import Tasks from "@/pages/Tasks";
import SettingsPage from "@/pages/Settings";
import AutoPublisher from "@/pages/AutoPublisher";
import KeywordResearch from "@/pages/KeywordResearch";
import BreakingNews from "@/pages/BreakingNews";
import Performance from "@/pages/Performance";
import InternalLinking from "@/pages/InternalLinking";
import ClickbaitOptimizer from "@/pages/ClickbaitOptimizer";
import TwitterAnalysis from "@/pages/TwitterAnalysis";
import SeoAgent from "@/pages/SeoAgent";
import AdvertiserOrders from "@/pages/AdvertiserOrders";
import MyNegotiations from "@/pages/MyNegotiations";
import ErrorBoundary from "@/components/ErrorBoundary";
import BugReportWidget from "@/components/BugReportWidget";
import DomainExpiringBanner from "@/components/DomainExpiringBanner";
import AftermarketDomains from "@/pages/AftermarketDomains";
import HuntedPortfolio from "@/pages/HuntedPortfolio";
import HuntedTips from "@/pages/HuntedTips";
import HuntedErrors from "@/pages/HuntedErrors";
import ApiDocsPage from "@/pages/ApiDocsPage";
import PublisherSandboxPage from "@/pages/PublisherSandboxPage";
import AdminApiIntegrations from "@/pages/AdminApiIntegrations";
import ZorkiPublicApi from "@/pages/ZorkiPublicApi";
import BatchDeploy from "@/pages/BatchDeploy";
import DomainHunter from "@/pages/DomainHunter";
import GamblingModeration from "@/pages/GamblingModeration";
import MailDrafts from "@/pages/MailDrafts";
import SeoDashboard from "@/pages/SeoDashboard";
import KeywordResearchTool from "@/pages/KeywordResearchTool";
import RankTrackerPage from "@/pages/RankTrackerPage";
import CannibalizationDashboard from "@/pages/CannibalizationDashboard";
import DailyReportPage from "@/pages/DailyReportPage";
import InsightsPage from "@/pages/InsightsPage";
import SmartPublishingPage from "@/pages/SmartPublishingPage";
import BulkNoindexPage from "@/pages/BulkNoindexPage";
import AutoReactivatorPage from "@/pages/AutoReactivatorPage";
import MoneyMapPage from "@/pages/MoneyMapPage";
import CrossPortalConfigPage from "@/pages/CrossPortalConfigPage";
import LiveDashboardPage from "@/pages/LiveDashboardPage";

// Strony publiczne (bez logowania)
const PUBLIC_PATHS = ["/", "/shop-public", "/blog", "/login", "/funkcje", "/regulamin", "/polityka-prywatnosci", "/api-docs", "/wydawcy-testy"];

// Sprawdz czy sciezka to /zamow/:id lub /blog/:id
const isOrderPath = (path) => path.startsWith("/zamow/");
const isBlogPath = (path) => path.startsWith("/blog");
const isFeaturesPath = (path) => path === "/funkcje";
const isLegalPath = (path) => path === "/regulamin" || path === "/polityka-prywatnosci";
const isApiDocsPath = (path) => path === "/api-docs" || path === "/wydawcy-testy";

function AppLayout() {
  const { isLoggedIn, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-background"><p className="text-muted-foreground">Ladowanie...</p></div>;
  }

  const isPublicPath = PUBLIC_PATHS.includes(location.pathname) || isOrderPath(location.pathname) || isBlogPath(location.pathname) || isFeaturesPath(location.pathname) || isLegalPath(location.pathname) || isApiDocsPath(location.pathname);

  // Strony publiczne - landing, sklep, blog, zamow, funkcje, legal
  if (!isLoggedIn && isPublicPath) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage onLogin={() => window.location.href = "/login"} />} />
        <Route path="/shop-public" element={<PublicShop onLogin={() => window.location.href = "/login"} />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postSlug" element={<BlogPage />} />
        <Route path="/funkcje" element={<FeaturesPage />} />
        <Route path="/regulamin" element={<LegalPage />} />
        <Route path="/polityka-prywatnosci" element={<LegalPage />} />
        <Route path="/login" element={<AuthForm />} />
        <Route path="/zamow/:siteId" element={<OrderFromEmbed />} />
        <Route path="/api-docs" element={<ApiDocsPage />} />
        <Route path="/wydawcy-testy" element={<PublisherSandboxPage />} />
      </Routes>
    );
  }

  // Niezalogowany na niepublicznej stronie -> login
  if (!isLoggedIn) {
    return <AuthForm />;
  }

  // Zalogowany - pelny dashboard
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <DomainExpiringBanner user={user} />
        <TopBar />
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/login" element={<Dashboard />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/shop-public" element={<ShopPage />} />
            <Route path="/my-orders" element={<AdvertiserOrders />} />
            <Route path="/my-negotiations" element={<MyNegotiations />} />
            <Route path="/zamowienia" element={<AdvertiserOrders />} />
            <Route path="/zamow/:siteId" element={<OrderFromEmbed />} />
            <Route path="/publisher" element={<PublisherDashboard />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/zorki" element={<AdminZorki />} />
            <Route path="/zorki" element={<ZorkiWallet />} />
            <Route path="/mail" element={<MailClient />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/sites" element={<Sites />} />
            <Route path="/html-generator" element={<HtmlGenerator />} />
            <Route path="/generator" element={<ContentGenerator />} />
            <Route path="/monitor" element={<AiMonitor />} />
            <Route path="/trends" element={<TrendingTopics />} />
            <Route path="/rewrite" element={<RewriteArticle />} />
            <Route path="/mass-generate" element={<MassGenerate />} />
            <Route path="/affiliates" element={<AffiliateProducts />} />
            <Route path="/woo-products" element={<WooProducts />} />
            <Route path="/social" element={<SocialMedia />} />
            <Route path="/reports" element={<MobileReports />} />
            <Route path="/comments" element={<Comments />} />
            <Route path="/forums" element={<Forums />} />
            <Route path="/rss" element={<RssFeeds />} />
            <Route path="/queue" element={<PostQueue />} />
            <Route path="/tasks" element={<Tasks />} />
            <Route path="/connectors" element={<ApiConnectors />} />
            <Route path="/extensions" element={<Extensions />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/media" element={<MediaLibrary />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/auto-publish" element={<AutoPublisher />} />
            <Route path="/keyword-research" element={<KeywordResearch />} />
            <Route path="/breaking-news" element={<BreakingNews />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/internal-linking" element={<InternalLinking />} />
            <Route path="/clickbait" element={<ClickbaitOptimizer />} />
            <Route path="/twitter-analysis" element={<TwitterAnalysis />} />
            <Route path="/seo-agent" element={<SeoAgent />} />
            <Route path="/aftermarket-domains" element={<AftermarketDomains />} />
            <Route path="/admin/hunted-portfolio" element={<HuntedPortfolio />} />
            <Route path="/admin/hunted-tips" element={<HuntedTips />} />
            <Route path="/admin/hunted-errors" element={<HuntedErrors />} />
            <Route path="/admin/api-integrations" element={<AdminApiIntegrations />} />
            <Route path="/admin/zorki-public-api" element={<ZorkiPublicApi />} />
            <Route path="/admin/batch-deploy" element={<BatchDeploy />} />
            <Route path="/admin/domain-hunter" element={<DomainHunter />} />
            <Route path="/admin/gambling-moderation" element={<GamblingModeration />} />
            <Route path="/mail-drafts" element={<MailDrafts />} />
            <Route path="/admin/seo-dashboard" element={<SeoDashboard />} />
            <Route path="/admin/keyword-research" element={<KeywordResearchTool />} />
            <Route path="/admin/rank-tracker" element={<RankTrackerPage />} />
            <Route path="/admin/cannibalization" element={<CannibalizationDashboard />} />
            <Route path="/admin/daily-report" element={<DailyReportPage />} />
            <Route path="/admin/insights" element={<InsightsPage />} />
            <Route path="/admin/smart-publishing" element={<SmartPublishingPage />} />
            <Route path="/admin/noindex" element={<BulkNoindexPage />} />
            <Route path="/admin/reactivator" element={<AutoReactivatorPage />} />
            <Route path="/admin/money" element={<MoneyMapPage />} />
            <Route path="/admin/cross-linking" element={<CrossPortalConfigPage />} />
            <Route path="/admin/live" element={<LiveDashboardPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/wydawcy-testy" element={<PublisherSandboxPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppLayout />
              <Toaster position="bottom-right" richColors />
              <BugReportWidget />
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
